<div class="trd-page">
  <app-project-nav></app-project-nav>

  @if (loading()) {
    <div class="trd-state">
      <mat-progress-spinner mode="indeterminate" diameter="40"></mat-progress-spinner>
    </div>
  } @else if (loadError()) {
    <div class="trd-state trd-state--error">
      <p class="trd-state-text" role="alert">{{ 'testResults.detail.loadError' | translate }}</p>
      <button type="button" lib-button variant="secondary" data-testid="result-retry" (click)="load()">
        {{ 'common.retry' | translate }}
      </button>
    </div>
  } @else if (notFound()) {
    <div class="trd-state trd-state--not-found">
      <p class="trd-state-text" role="alert" data-testid="result-not-found">
        {{ 'testResults.detail.notFound' | translate }}
      </p>
      <a lib-button variant="secondary" data-testid="result-not-found-back" [routerLink]="overviewLink">
        {{ 'testResults.detail.backToOverview' | translate }}
      </a>
    </div>
  } @else {
    <div class="trd-header">
      <div class="trd-heading">
        <div class="trd-eyebrow">
          @if (projectName(); as name) {
            {{ name }} ·
          }
          {{ 'testResults.detail.eyebrow' | translate }}
        </div>
        <div class="trd-title-row">
          <h1 class="trd-title">{{ 'testResults.detail.title' | translate }}</h1>
          <code class="trd-run-key">{{ runKey() }}</code>
          <span class="trd-art" [class.trd-art--auto]="automated()">
            <mat-icon aria-hidden="true">{{ automated() ? 'settings' : 'manage_accounts' }}</mat-icon>
            {{ (automated() ? 'testResults.detail.automated' : 'testResults.detail.manual') | translate }}
          </span>
        </div>
        <div class="trd-subtitle" data-testid="result-header-title">
          @if (multiCase()) {
            @if (runName()) {
              {{ runName() }} ·
            }
            {{ 'testResults.detail.caseCount' | translate: { count: caseCount() } }}
          } @else {
            {{ caseTitle() }}
            @if (caseKey()) {
              ·
              <code class="trd-case-key">{{ caseKey() }}</code>
            }
          }
        </div>
      </div>
      <div class="trd-header-actions">
        @if (!multiCase()) {
          <ng-container
            [ngTemplateOutlet]="protocolButton"
            [ngTemplateOutletContext]="{ $implicit: caseGroups()[0]?.id ?? null }"
          />
          <ng-container
            [ngTemplateOutlet]="historyLink"
            [ngTemplateOutletContext]="{ $implicit: caseGroups()[0]?.testCaseId ?? null }"
          />
        }
        <a
          lib-button
          variant="secondary"
          class="trd-back"
          [routerLink]="overviewLink"
          [queryParams]="overviewQueryParams"
        >
          <mat-icon class="trd-action-icon" aria-hidden="true">chevron_right</mat-icon>
          {{ 'testResults.detail.backToOverview' | translate }}
        </a>
      </div>
    </div>

    <div class="trd-card trd-summary">
      <div class="trd-summary-main">
        <div class="trd-result-circle trd-result-circle--{{ statusKey(outcome()) }}">
          <mat-icon aria-hidden="true">{{ outcomeIcon() }}</mat-icon>
        </div>
        <div>
          <span class="trd-pill trd-pill--{{ statusKey(outcome()) }}">
            <span class="trd-pill-dot"></span>
            {{ 'testResults.result.' + statusKey(outcome()) | translate }}
          </span>
          <div class="trd-passed">
            {{ 'testResults.detail.summaryPassed' | translate: { passed: passed(), total: total() } }}
          </div>
        </div>
        <span class="trd-flex"></span>
        <div class="trd-metrics">
          <div class="trd-metric">
            <div class="trd-metric-label">{{ 'testResults.detail.metric.environment' | translate }}</div>
            <div class="trd-metric-value">{{ environment() }}</div>
          </div>
          <div class="trd-metric">
            <div class="trd-metric-label">{{ 'testResults.detail.metric.duration' | translate }}</div>
            <div class="trd-metric-value">{{ runDuration() }}</div>
          </div>
          <div class="trd-metric">
            <div class="trd-metric-label">{{ 'testResults.detail.metric.failed' | translate }}</div>
            <div class="trd-metric-value">{{ failed() }}</div>
          </div>
          <div class="trd-metric">
            <div class="trd-metric-label">{{ 'testResults.detail.metric.skipped' | translate }}</div>
            <div class="trd-metric-value">{{ skipped() }}</div>
          </div>
        </div>
      </div>

      <div class="trd-meta">
        <span class="trd-source">
          <span class="trd-source-glyph trd-source-glyph--{{ systemKey() }}">
            {{ system().glyph }}
          </span>
          <strong>{{ system().labelKey | translate }}</strong>
        </span>
        @if (pipeline(); as ci) {
          <span class="trd-sep">·</span>
          <a
            class="trd-pipeline"
            data-testid="result-pipeline"
            [href]="ci.runUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            <code>{{ ci.pipeline }} {{ ci.buildNumber }}</code>
            <span aria-hidden="true">↗</span>
          </a>
        }
        @if (executedByName()) {
          <span class="trd-sep">·</span>
          <app-avatar [name]="executedByName()" [size]="22"></app-avatar>
          <span class="trd-exec">{{ executedByName() }}</span>
        }
        @if (completedAt()) {
          <span class="trd-sep">·</span>
          <span class="trd-when">{{ completedAt() | date: 'dd.MM.yyyy, HH:mm' }}</span>
        }
        @for (tag of tags(); track tag) {
          <span class="trd-tag" data-testid="result-tag">#{{ tag }}</span>
        }
        @if (unmatchedTests(); as count) {
          <span class="trd-sep">·</span>
          <a class="trd-unmatched" data-testid="unmatched-tests-hint" [routerLink]="ciMappingLink">
            {{ unmatchedTestsKey() | translate: { count } }}
            <span aria-hidden="true">›</span>
          </a>
        }
        <span class="trd-flex"></span>
        @if (finished()) {
          <mat-icon class="trd-frozen-icon" aria-hidden="true">shield</mat-icon>
          <span class="trd-frozen" data-testid="result-frozen">{{ 'testResults.detail.frozen' | translate }}</span>
        }
      </div>

      <div class="trd-stats">
        <span class="trd-stats-label">{{ 'testResults.detail.stats.label' | translate }}</span>
        <span class="trd-stat trd-stat--total">
          {{ 'testResults.detail.stats.total' | translate }}
          <strong>{{ total() }}</strong>
        </span>
        <span class="trd-stat trd-stat--pass">
          {{ 'testResults.detail.stats.pass' | translate }}
          <strong>{{ passed() }}</strong>
        </span>
        <span class="trd-stat trd-stat--fail">
          {{ 'testResults.detail.stats.fail' | translate }}
          <strong>{{ failed() }}</strong>
        </span>
        <span class="trd-stat trd-stat--blocked">
          {{ 'testResults.detail.stats.blocked' | translate }}
          <strong>{{ blocked() }}</strong>
        </span>
        <span class="trd-stat trd-stat--skip">
          {{ 'testResults.detail.stats.skip' | translate }}
          <strong>{{ skipped() }}</strong>
        </span>
      </div>
    </div>

    <h2 class="trd-steps-heading">{{ 'testResults.detail.stepsHeading' | translate }}</h2>
    <div class="trd-card">
      @if (multiCase()) {
        @for (group of caseGroups(); track group.id) {
          <section class="trd-case" data-testid="result-case-group">
            <div class="trd-case-head">
              @if (group.key) {
                <code class="trd-case-head-key">{{ group.key }}</code>
              }
              <span class="trd-case-head-title">{{ group.title }}</span>
              @if (group.configuration; as configuration) {
                <app-konfig-pill [configuration]="configuration" />
              }
              <span class="trd-pill trd-pill--{{ statusKey(group.status) }} trd-case-pill" data-testid="case-status">
                <span class="trd-pill-dot"></span>
                {{ 'testResults.result.' + statusKey(group.status) | translate }}
              </span>
              <ng-container
                [ngTemplateOutlet]="historyLink"
                [ngTemplateOutletContext]="{ $implicit: group.testCaseId }"
              />
              <ng-container [ngTemplateOutlet]="protocolButton" [ngTemplateOutletContext]="{ $implicit: group.id }" />
            </div>
            <ng-container [ngTemplateOutlet]="stepsHead" />
            @if ((group.rows ?? group.steps).length === 0) {
              <div class="trd-empty">{{ 'testResults.detail.caseEmpty' | translate }}</div>
            }
            @if (group.rows; as rows) {
              <ng-container [ngTemplateOutlet]="treeRowsTpl" [ngTemplateOutletContext]="{ $implicit: rows }" />
            } @else {
              <ng-container [ngTemplateOutlet]="stepRows" [ngTemplateOutletContext]="{ $implicit: group.steps }" />
            }
          </section>
        }
      } @else {
        <ng-container [ngTemplateOutlet]="stepsHead" />
        @if ((treeRows() ?? steps()).length === 0) {
          <div class="trd-empty">{{ 'testResults.detail.empty' | translate }}</div>
        }
        @if (treeRows(); as rows) {
          <ng-container [ngTemplateOutlet]="treeRowsTpl" [ngTemplateOutletContext]="{ $implicit: rows }" />
        } @else {
          <ng-container [ngTemplateOutlet]="stepRows" [ngTemplateOutletContext]="{ $implicit: steps() }" />
        }
      }
    </div>

    <section class="trd-card trd-comments">
      <h2 class="trd-comments-title">{{ 'testResults.detail.comments.title' | translate }}</h2>
      <p class="trd-comments-subtitle">{{ 'testResults.detail.comments.subtitle' | translate }}</p>
      @switch (commentsState()) {
        @case ('loading') {
          <mat-progress-spinner
            data-testid="result-comments-loading"
            mode="indeterminate"
            diameter="24"
            [attr.aria-label]="'testResults.detail.comments.loading' | translate"
          ></mat-progress-spinner>
        }
        @case ('error') {
          <p class="trd-comments-error" role="alert" data-testid="result-comments-error">
            {{ 'testResults.detail.comments.loadError' | translate }}
          </p>
          <ng-container *ngTemplateOutlet="commentsRetry"></ng-container>
        }
        @default {
          @for (entry of commentEntries(); track entry.comment.id) {
            @let author = entry.comment.authorName ?? ('testRun.comments.handoverUnknownRecipient' | translate);
            <article
              class="trd-comment-entry"
              data-testid="result-comment"
              [class.trd-comment-entry--handover]="entry.handoverKey"
            >
              <app-avatar [name]="author" [size]="28"></app-avatar>
              <div class="trd-comment-body">
                <div class="trd-comment-head">
                  <span class="trd-comment-author">{{ author }}</span>
                  @if (entry.handoverKey; as key) {
                    <span class="trd-comment-handover" data-testid="result-comment-handover">
                      {{ 'testResults.detail.comments.handover' | translate: { key } }}
                    </span>
                  } @else if (entry.step; as step) {
                    <span class="trd-comment-step" data-testid="result-comment-step">
                      @if (step.caseKey) {
                        <code>{{ step.caseKey }}</code>
                        ·
                      }
                      <span class="trd-comment-step-title">{{ step.caseTitle }}</span>
                      · {{ 'testResults.detail.comments.step' | translate: { number: step.number } }}
                    </span>
                  }
                  @if (entry.step?.status; as status) {
                    <span class="trd-pill trd-pill--{{ statusKey(status) }} trd-comment-status">
                      {{ 'testResults.result.' + statusKey(status) | translate }}
                    </span>
                  }
                  <span class="trd-comment-when">{{ entry.comment.createdAt | date: 'dd.MM.yyyy HH:mm' }}</span>
                </div>
                @if (entry.handoverKey) {
                  @let recipient =
                    entry.comment.toGroup ||
                    entry.comment.toUserName ||
                    ('testRun.comments.handoverUnknownRecipient' | translate);
                  <p class="trd-comment-text">
                    {{ 'testRun.comments.handoverTo' | translate: { name: recipient }
                    }}{{ entry.comment.text ? ': ' + entry.comment.text : '' }}
                  </p>
                } @else {
                  <p class="trd-comment-text">{{ entry.comment.text }}</p>
                }
                @if (entry.comment.attachments.length > 0) {
                  <div class="trd-comment-files">
                    @for (file of entry.comment.attachments; track file.id) {
                      <span class="trd-comment-file" data-testid="result-comment-file">
                        <mat-icon aria-hidden="true">image</mat-icon>
                        {{ file.originalFilename }}
                      </span>
                    }
                  </div>
                }
              </div>
            </article>
          } @empty {
            @if (!stepCommentsFailed()) {
              <p class="trd-comments-empty" data-testid="result-comments-empty">
                {{ 'testResults.detail.comments.empty' | translate }}
              </p>
            }
          }
          @if (stepCommentsFailed()) {
            <p class="trd-comments-error" role="alert" data-testid="result-step-comments-error">
              {{ 'testResults.detail.comments.stepLoadError' | translate }}
            </p>
            <ng-container *ngTemplateOutlet="commentsRetry"></ng-container>
          }
        }
      }
      <ng-template #commentsRetry>
        <button
          type="button"
          lib-button
          variant="secondary"
          data-testid="result-comments-retry"
          (click)="retryComments()"
        >
          {{ 'common.retry' | translate }}
        </button>
      </ng-template>
    </section>
  }

  <ng-template #stepsHead>
    <div class="trd-steps-head">
      <div class="trd-col-nr">{{ 'testResults.detail.table.number' | translate }}</div>
      <div class="trd-col-step">{{ 'testResults.detail.table.step' | translate }}</div>
      <div class="trd-col-duration">{{ 'testResults.detail.table.duration' | translate }}</div>
      <div class="trd-col-result">{{ 'testResults.detail.table.result' | translate }}</div>
    </div>
  </ng-template>

  <ng-template #stepRows appStepRows let-rows>
    @for (step of rows; track step.result.id) {
      @if (step.showGroupHeader) {
        <div class="trd-group" data-testid="step-group">
          <mat-icon aria-hidden="true">content_copy</mat-icon>
          <span>{{ step.group }}</span>
          @if (step.groupIteration !== null) {
            <span data-testid="group-iteration">
              {{ 'testResults.detail.iteration' | translate: { n: step.groupIteration } }}
            </span>
          }
          @if (step.groupStatus) {
            <span
              class="trd-pill trd-pill--{{ statusKey(step.groupStatus) }} trd-group-pill"
              data-testid="group-status"
            >
              <span class="trd-pill-dot"></span>
              {{ 'testResults.result.' + statusKey(step.groupStatus) | translate }}
            </span>
          }
        </div>
      }
      <ng-container [ngTemplateOutlet]="stepRow" [ngTemplateOutletContext]="{ $implicit: step }" />
    }
  </ng-template>

  <ng-template #stepRow appStepRow let-step>
    <div class="trd-step">
      <div
        class="trd-col-nr {{ step.level === null ? '' : 'trd-col-nr--level-' + step.level }}"
        [class.trd-col-nr--indent]="step.indented"
      >
        {{ step.number }}
      </div>
      <div class="trd-col-step">
        <div class="trd-step-text" data-testid="step-text">{{ step.result.descriptionSnapshot }}</div>
        @if (step.result.expectedSnapshot) {
          <div class="trd-soll">
            <strong>{{ 'testResults.detail.soll' | translate }}</strong>
            {{ step.result.expectedSnapshot }}
          </div>
        }
        @if (step.result.actualResult) {
          <div class="trd-ist" [class.trd-ist--fail]="step.result.status === 'FAILED'">
            <strong>{{ 'testResults.detail.ist' | translate }}</strong>
            {{ step.result.actualResult }}
          </div>
        }
        @if (step.result.comment) {
          <div class="trd-comment">{{ step.result.comment }}</div>
        }
        @if (evidenceFor(step.result.id).length > 0) {
          <div class="trd-evidence">
            @for (item of evidenceFor(step.result.id); track item.id) {
              <img
                class="trd-evidence-thumb"
                [src]="item.url"
                [alt]="item.filename"
                role="button"
                tabindex="0"
                [attr.aria-label]="'testResults.detail.evidenceView' | translate"
                (click)="openLightbox(item.url)"
                (keydown.enter)="openLightbox(item.url)"
              />
            }
          </div>
        }
        @if (step.result.status === 'FAILED') {
          <div class="trd-bug">
            @if (linkedBugKey(step.result); as key) {
              @if (linkedBugUrl(step.result); as url) {
                <a
                  class="trd-bug__linked"
                  role="button"
                  tabindex="0"
                  [attr.aria-label]="'testResults.detail.bug.jumpAria' | translate: { key: key }"
                  (click)="openLinkedBug(url)"
                  (keydown.enter)="openLinkedBug(url)"
                >
                  🐞 {{ 'testResults.detail.bug.linked' | translate: { key: key } }}
                </a>
              } @else {
                <span class="trd-bug__linked trd-bug__linked--plain" data-testid="bug-key-plain">
                  🐞 {{ 'testResults.detail.bug.linkedPlain' | translate: { key: key } }}
                </span>
              }
            } @else if (!isArchived() && !isReader()) {
              @if (bugProviders().length === 1) {
                <button
                  type="button"
                  class="trd-bug__create"
                  (click)="openBugDialog(step.result, step.number, bugProviders()[0])"
                >
                  <mat-icon aria-hidden="true">add</mat-icon>
                  {{ 'testResults.detail.bug.createIn' | translate: { provider: providerLabel(bugProviders()[0]) } }}
                </button>
              } @else if (bugProviders().length > 1) {
                <span class="trd-bug__trackers">
                  <span class="trd-bug__tracker-label">{{ 'testResults.detail.bug.create' | translate }}</span>
                  @for (provider of bugProviders(); track provider) {
                    <button
                      type="button"
                      class="trd-bug__tracker"
                      [style.color]="providerColor(provider)"
                      (click)="openBugDialog(step.result, step.number, provider)"
                    >
                      <span class="trd-bug__tracker-mark" aria-hidden="true"></span>
                      {{ providerLabel(provider) }}
                    </button>
                  }
                </span>
              } @else if (noBugTarget()) {
                <span class="trd-bug__hint" data-testid="bug-no-tracker">
                  {{ 'testResults.detail.bug.noIntegration' | translate }}
                </span>
              } @else if (bugTargetsFailed()) {
                <span class="trd-bug__hint" data-testid="bug-targets-error">
                  {{ 'testResults.detail.bug.targetsLoadError' | translate }}
                </span>
              }
            }
          </div>
        }
        @if ((step.result.defects?.length ?? 0) > 0 || canCaptureDefect(step.result)) {
          <div class="trd-defects">
            @if (step.result.defects; as defects) {
              <app-defect-ref-chips [projectId]="projectId" [defects]="defects" />
            }
            @if (canCaptureDefect(step.result)) {
              <button
                type="button"
                class="trd-defects__capture"
                data-testid="defect-erfassen"
                [attr.aria-label]="'defect.capture.buttonAria' | translate: { n: step.number }"
                (click)="captureDefect(step.result)"
              >
                <mat-icon aria-hidden="true">bug_report</mat-icon>
                {{ 'defect.capture.button' | translate }}
              </button>
            }
          </div>
        }
      </div>
      <div class="trd-col-duration" [title]="stepDuration(step.result)">{{ stepDuration(step.result) }}</div>
      <div class="trd-col-result">
        <span class="trd-pill trd-pill--{{ statusKey(step.result.status) }}">
          <span class="trd-pill-dot"></span>
          {{ 'testResults.result.' + statusKey(step.result.status) | translate }}
        </span>
      </div>
    </div>
  </ng-template>

  <ng-template #treeRowsTpl appTreeRows let-rows>
    @for (row of rows; track row.item.result.id) {
      @if (isShown(row)) {
        @switch (row.kind) {
          @case ('step') {
            <ng-container [ngTemplateOutlet]="stepRow" [ngTemplateOutletContext]="{ $implicit: row.item }" />
          }
          @case ('call') {
            <div
              class="trd-call trd-call--level-{{ row.level }}"
              data-testid="call-skipped"
              [attr.data-call-state]="row.item.result.callState"
              [title]="lineOf(row.item.result)"
            >
              <div class="trd-col-nr"></div>
              <div class="trd-col-step">
                <span class="trd-call__module">{{ row.item.result.descriptionSnapshot }}</span>
                –
                <span data-testid="call-skipped-reason">{{ reasonOf(row.item.result) }}</span>
              </div>
              <div class="trd-col-duration"></div>
              <div class="trd-col-result">
                <span class="trd-pill trd-pill--{{ statusKey(row.item.result.status) }}">
                  <span class="trd-pill-dot"></span>
                  {{ 'testResults.result.' + statusKey(row.item.result.status) | translate }}
                </span>
              </div>
            </div>
          }
          @case ('group') {
            <ng-container [ngTemplateOutlet]="moduleHeader" [ngTemplateOutletContext]="{ $implicit: row }" />
          }
          @case ('loop') {
            <ng-container [ngTemplateOutlet]="moduleHeader" [ngTemplateOutletContext]="{ $implicit: row }" />
          }
          @default {
            <div
              class="trd-group trd-group--level-{{ row.level }}"
              [class.trd-group--pass]="row.kind !== 'return'"
              [class.trd-group--return]="row.kind === 'return'"
              [attr.data-testid]="
                row.kind === 'return'
                  ? 'group-return-block'
                  : (row.kind === 'pass' ? 'group-pass-' : 'group-return-pass-') + row.pass
              "
            >
              <button
                type="button"
                class="trd-group__toggle"
                [attr.aria-expanded]="!collapsed().has(row.item.result.id)"
                (click)="toggle(row.item.result.id)"
              >
                <mat-icon aria-hidden="true">
                  {{ collapsed().has(row.item.result.id) ? 'expand_more' : 'expand_less' }}
                </mat-icon>
                {{ passLabelOf(row) }}
              </button>
              @if (row.kind !== 'return') {
                <span
                  class="trd-pill trd-pill--{{ statusKey(row.item.result.status) }} trd-group-pill"
                  data-testid="group-status"
                >
                  <span class="trd-pill-dot"></span>
                  {{ 'testResults.result.' + statusKey(row.item.result.status) | translate }}
                </span>
              }
            </div>
          }
        }
      }
    }
  </ng-template>

  <ng-template #moduleHeader let-row>
    <div class="trd-group trd-group--level-{{ row.level }}" data-testid="step-group">
      <mat-icon aria-hidden="true">content_copy</mat-icon>
      <span class="trd-group__path" data-testid="group-path">
        @for (part of headerOf(row.item.result).parts; track $index) {
          <span>{{ part }}</span>
          ›
        }
        <strong class="trd-group__path-last">{{ headerOf(row.item.result).last }}</strong>
      </span>
      @if (row.kind === 'loop') {
        <span aria-hidden="true">·</span>
        <span data-testid="group-rounds">
          {{ (row.rounds === 1 ? 'runTree.rounds.one' : 'runTree.rounds.other') | translate: { count: row.rounds } }}
        </span>
      }
      @if ((row.item.result.iterationIndex ?? null) !== null) {
        <span data-testid="group-iteration">
          {{ 'testResults.detail.iteration' | translate: { n: row.item.result.iterationIndex } }}
        </span>
      }
      @if (row.item.result.status) {
        <span
          class="trd-pill trd-pill--{{ statusKey(row.item.result.status) }} trd-group-pill"
          data-testid="group-status"
        >
          <span class="trd-pill-dot"></span>
          {{ 'testResults.result.' + statusKey(row.item.result.status) | translate }}
        </span>
      }
    </div>
  </ng-template>

  @if (lightboxUrl(); as url) {
    <div
      class="trd-lightbox"
      role="button"
      tabindex="0"
      [attr.aria-label]="'testResults.detail.lightboxClose' | translate"
      (click)="closeLightbox()"
      (keydown.enter)="closeLightbox()"
      (keydown.escape)="closeLightbox()"
    >
      <img class="trd-lightbox-img" [src]="url" [alt]="'testResults.detail.lightboxAlt' | translate" />
    </div>
  }
</div>

<app-bugticket-dialog
  [projectId]="projectId"
  [targets]="bugTargets()"
  [request]="bugDialog()"
  (created)="onBugCreated($event)"
  (closed)="bugDialog.set(null)"
/>

<app-defect-erfassen-dialog
  [projectId]="projectId"
  [stepResultId]="defectStepId()"
  (created)="onDefectCreated($event)"
  (closed)="defectStepId.set(null)"
/>

@if (protocolExecution(); as execution) {
  <app-testprotokoll-dialog
    [projectId]="projectId"
    [runId]="runId"
    [runKey]="runKey()"
    [executionId]="execution.id"
    [executionStatus]="execution.status"
    [caseResultId]="protocolCaseResultId()"
    [(visible)]="protocolVisible"
  />
}

<ng-template #historyLink let-testCaseId>
  @if (testCaseId) {
    <a lib-button variant="secondary" data-testid="result-history" [routerLink]="historyRoute(testCaseId)">
      <mat-icon class="trd-action-icon" aria-hidden="true">bar_chart</mat-icon>
      {{ 'testResults.detail.history' | translate }}
    </a>
  }
</ng-template>

<ng-template #protocolButton let-caseResultId>
  <button
    type="button"
    lib-button
    variant="secondary"
    data-testid="testprotokoll-open"
    [disabled]="!protocolExecution() || !caseResultId"
    [attr.title]="protocolExecution() ? null : ('testResults.detail.protocolUnavailable' | translate)"
    (click)="openProtocol(caseResultId)"
  >
    <span aria-hidden="true">↓</span>
    {{ 'testResults.detail.protocolOpen' | translate }}
  </button>
</ng-template>
